import React from 'react'
import { DownOutlined } from '@ant-design/icons'
import styled, { css } from 'styled-components'

/**
 * Flex container that prevents overflow by allowing the flex item to shrink
 * Use this for cell content that should not overflow the cell boundaries
 */
export const FlexCellContent = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  min-width: 0;
  gap: 4px;
`

/**
 * Text content that can be truncated with ellipsis when it overflows
 */
export const TruncatableText = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

/**
 * Container for buttons or other elements that should not shrink
 */
export const NoShrinkContainer = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
`

/**
 * Small color swatch square, shared between ColorCellRenderer and ColorPickerEditor trigger.
 */
export const ColorSwatch: React.FC<{ color: string }> = ({ color }) => (
  <div
    style={{
      width: 20,
      height: 20,
      backgroundColor: color,
      border: '1px solid #d9d9d9',
      borderRadius: 4,
      flexShrink: 0
    }}
  />
)

/**
 * Shared layout for dropdown-style cell renderers (select, cascader, tree select).
 * Renders children with a DownOutlined icon on the right, with text truncation.
 */
export const DropdownCellLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', height: '100%' }}
  >
    <TruncatableText>{children}</TruncatableText>
    <DownOutlined style={{ fontSize: 10, opacity: 0.45, marginLeft: 4, flexShrink: 0 }} />
  </div>
)

/**
 * CSS mixin for ag-Grid group cells that need action buttons aligned to the right.
 * Apply this to a container wrapping the grid.
 *
 * Usage:
 * 1. Add cellClass: 'ag-group-cell-with-actions' to your autoGroupColumnDef
 * 2. Wrap your grid with a styled component that includes this mixin
 *
 * Example:
 * ```tsx
 * const GridWrapper = styled.div`
 *   ${groupCellWithActionsStyles}
 * `
 *
 * autoGroupColumnDef={{
 *   cellClass: 'ag-group-cell-with-actions',
 *   cellRendererParams: {
 *     innerRenderer: (params) => (
 *       <FlexCellContent>
 *         <TruncatableText>{params.value}</TruncatableText>
 *         <NoShrinkContainer>
 *           <Button icon={<PlusOutlined />} />
 *         </NoShrinkContainer>
 *       </FlexCellContent>
 *     )
 *   }
 * }}
 * ```
 */
export const groupCellWithActionsStyles = css`
  .ag-group-cell-with-actions {
    .ag-cell-wrapper {
      width: 100%;
    }
    .ag-group-value {
      flex: 1;
      overflow: hidden;
    }
  }
`
